'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { CommandPaletteTriggerButton } from './command-palette';
import { LanguageSwitcherNav } from './language-switcher-nav';
import { Link } from './link';
import { Logo } from './logo';
import { MobileNavMenu } from './mobile-nav-menu';
import { MobileNavPanel } from './mobile-nav-panel';
import { NavbarAccountButton, NavbarSystemLink } from './navbar-account-button';
import { PlusGrid, PlusGridItem, PlusGridRow } from './plus-grid';
import { ThemeToggle } from './theme-toggle';
import { useNavLinks } from './use-nav-links';

function LeftDesktopNav(): React.JSX.Element {
  const { left } = useNavLinks();
  return (
    <nav className="relative hidden lg:flex items-center">
      {left.map(({ href, label }) => (
        <PlusGridItem key={href} className="relative flex">
          <Link
            href={href}
            className="cm-underline flex items-center px-4 py-3 text-base font-medium text-foreground bg-blend-multiply data-hover:bg-accent/50"
          >
            {label}
          </Link>
        </PlusGridItem>
      ))}
    </nav>
  );
}

function DesktopNav(): React.JSX.Element {
  const { right } = useNavLinks();
  return (
    <nav className="relative hidden lg:flex items-center">
      {right.map(({ href, label }) => (
        <PlusGridItem key={href} className="relative flex">
          <Link
            href={href}
            className="cm-underline flex items-center px-4 py-3 text-base font-medium text-foreground bg-blend-multiply data-hover:bg-accent/50"
          >
            {label}
          </Link>
        </PlusGridItem>
      ))}
      <PlusGridItem className="relative flex">
        <NavbarSystemLink />
      </PlusGridItem>
      <PlusGridItem className="relative flex items-center">
        <CommandPaletteTriggerButton variant="icon" />
      </PlusGridItem>
      <PlusGridItem className="relative flex items-center">
        <ThemeToggle />
      </PlusGridItem>
      <PlusGridItem className="relative flex items-center">
        <LanguageSwitcherNav />
      </PlusGridItem>
      <PlusGridItem className="relative flex">
        <NavbarAccountButton />
      </PlusGridItem>
    </nav>
  );
}

export function Navbar({ banner }: { banner?: React.ReactNode }): React.JSX.Element {
  const t = useTranslations('nav');
  const { left, right } = useNavLinks();
  const [open, setOpen] = useState(false);
  return (
    <header className="pt-12 sm:pt-16">
      <PlusGrid>
        <PlusGridRow className="relative flex items-center justify-between">
          <div className="relative flex items-center gap-6">
            <PlusGridItem className="py-3">
              <Link href="/" title={t('homeTitle')} className="cm-press inline-flex">
                <Logo className="h-9" />
              </Link>
            </PlusGridItem>
            <LeftDesktopNav />
            {banner && <div className="relative hidden items-center py-3 lg:flex">{banner}</div>}
          </div>
          <DesktopNav />
          <MobileNavMenu open={open} onToggle={(): void => setOpen((prev) => !prev)} />
        </PlusGridRow>
        <MobileNavPanel open={open} links={[...left, ...right]} />
      </PlusGrid>
    </header>
  );
}
